import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import {
  AppointmentType,
  AvailabilityDay,
  BookingApiError,
  BookingCreateResponse,
  createBooking,
  getAppointmentTypes,
  getAvailability,
} from '../api/bookingApi';
import { getContentByType } from '../api/contentApi';

type Step = 'type' | 'date' | 'slot' | 'details' | 'success';

const AppointmentScheduler: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const [step, setStep] = useState<Step>('type');
  const [loadError, setLoadError] = useState('');

  const [types, setTypes] = useState<AppointmentType[]>([]);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [selectedType, setSelectedType] = useState<AppointmentType | null>(null);

  const [month, setMonth] = useState<Date>(new Date());
  const [availability, setAvailability] = useState<AvailabilityResponseState>({});
  const [timeZoneId, setTimeZoneId] = useState<string | undefined>(undefined);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', website: '' });
  const [consent, setConsent] = useState(false);
  const [legalUrls, setLegalUrls] = useState<{ privacy: string; terms: string; cancellation: string }>({
    privacy: '#',
    terms: '#',
    cancellation: '#',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState<BookingCreateResponse | null>(null);

  // ── Load appointment types ──────────────────────────────────────────────────────
  useEffect(() => {
    getAppointmentTypes()
      .then((data) => setTypes(data))
      .catch(() => setLoadError(t('booking.errors.loadTypes')))
      .finally(() => setTypesLoaded(true));
  }, [t]);

  useEffect(() => {
    getContentByType('settings').then((docs) => {
      if (docs.length === 0) return;
      const doc = docs[0];
      const trans =
        doc.translations?.[currentLang] ??
        doc.translations?.['de'] ??
        Object.values(doc.translations ?? {})[0];
      if (trans) {
        setLegalUrls({
          privacy: trans.urlPrivacy || '#',
          terms: trans.urlTerms || '#',
          cancellation: trans.urlCancellation || '#',
        });
      }
    }).catch(() => {
      // fallback — legal URLs remain '#'
    });
  }, [currentLang]);

  // ── Load availability for the visible month whenever the type or month changes ──
  const loadAvailability = useCallback(
    (type: AppointmentType, visibleMonth: Date) => {
      const from = startOfMonthIso(visibleMonth);
      const to = endOfMonthIso(visibleMonth);
      setLoadingAvailability(true);
      getAvailability(type.id, from, to)
        .then((res) => {
          const byDate: AvailabilityResponseState = {};
          res.days.forEach((d) => { byDate[d.date] = d; });
          setAvailability(byDate);
          setTimeZoneId(res.timeZoneId);
        })
        .catch(() => setLoadError(t('booking.errors.loadAvailability')))
        .finally(() => setLoadingAvailability(false));
    },
    [t],
  );

  useEffect(() => {
    if (selectedType && step === 'date') {
      loadAvailability(selectedType, month);
    }
  }, [selectedType, month, step, loadAvailability]);

  const availableDates = useMemo(
    () => new Set(Object.keys(availability).filter((d) => availability[d].slots.length > 0)),
    [availability],
  );

  const isDayDisabled = (date: Date) => {
    // Don't paint every day as disabled while the month's availability is still loading — that
    // looks like "fully booked" rather than "loading". Availability data for a not-yet-loaded
    // month simply isn't checked here yet; the slot list re-validates once a day is picked.
    if (loadingAvailability) return false;
    const key = toDateKey(date);
    return !availableDates.has(key);
  };

  const slotsForSelectedDate = selectedDate ? availability[toDateKey(selectedDate)]?.slots ?? [] : [];

  // ── Step handlers ──────────────────────────────────────────────────────
  const handleSelectType = (type: AppointmentType) => {
    setSelectedType(type);
    setSelectedDate(undefined);
    setSelectedSlot(null);
    setStep('date');
  };

  const handleSelectDate = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    if (date) setStep('slot');
  };

  const handleSelectSlot = (slot: string) => {
    setSelectedSlot(slot);
    setStep('details');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !selectedSlot) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await createBooking({
        appointmentTypeId: selectedType.id,
        startUtc: selectedSlot,
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        message: form.message || undefined,
        preferredLanguage: currentLang,
        consent,
        website: form.website,
      });
      setResult(response);
      setStep('success');
    } catch (err) {
      if (err instanceof BookingApiError && err.status === 409) {
        setSubmitError(t('booking.errors.slotTaken'));
        // Refresh the day so the taken slot disappears from the list.
        if (selectedType && selectedDate) loadAvailability(selectedType, month);
        setStep('slot');
        setSelectedSlot(null);
      } else if (err instanceof BookingApiError && err.status === 503) {
        setSubmitError(t('booking.errors.temporarilyUnavailable'));
      } else {
        setSubmitError(err instanceof Error ? err.message : t('booking.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = (type: AppointmentType) => type.translations[currentLang]?.title || type.translations.de?.title || type.id;
  const description = (type: AppointmentType) => type.translations[currentLang]?.description || type.translations.de?.description;

  return (
    <div>
      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/booking.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('booking.title')}</h1>
        </div>
      </section>

      <div className="max-w-2xl mx-auto py-12 px-4 min-h-[60vh]">
      {loadError && types.length === 0 && <div className="error">{loadError}</div>}
      {step !== 'success' && <StepIndicator step={step} t={t} />}

      {step === 'type' && (
        <div className="space-y-3">
          {!typesLoaded && <p className="text-gray-500">{t('common.loading')}</p>}
          {typesLoaded && types.length === 0 && !loadError && (
            <p className="text-gray-500">{t('booking.noAppointmentTypes')}</p>
          )}
          {types.map((type) => (
            <button
              key={type.id}
              onClick={() => handleSelectType(type)}
              className="w-full text-left border border-gray-200 rounded-lg p-4 hover:border-primary-500 hover:bg-primary-50 transition"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-800">{title(type)}</span>
                <span className="text-sm text-gray-500">{type.durationMinutes} {t('booking.minutes')}</span>
              </div>
              {description(type) && <p className="text-sm text-gray-500 mt-1">{description(type)}</p>}
            </button>
          ))}
        </div>
      )}

      {step === 'date' && selectedType && (
        <div>
          <BackButton onClick={() => setStep('type')} t={t} />
          <p className="text-gray-600 mb-4">{title(selectedType)} — {selectedType.durationMinutes} {t('booking.minutes')}</p>
          {loadingAvailability && <p className="text-sm text-gray-400 mb-2">{t('common.loading')}</p>}
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleSelectDate}
            month={month}
            onMonthChange={setMonth}
            disabled={isDayDisabled}
            weekStartsOn={1}
          />
          {loadError && <div className="error mt-4">{loadError}</div>}
        </div>
      )}

      {step === 'slot' && selectedType && selectedDate && (
        <div>
          <BackButton onClick={() => setStep('date')} t={t} />
          <p className="text-gray-600 mb-4">
            {title(selectedType)} — {selectedDate.toLocaleDateString(currentLang)}
          </p>
          {submitError && <div className="error mb-4">{submitError}</div>}
          {timeZoneId && <p className="text-xs text-gray-400 mb-2">{t('booking.timesShownIn', { zone: timeZoneId })}</p>}
          {slotsForSelectedDate.length === 0 && <p className="text-gray-500">{t('booking.noSlotsForDay')}</p>}
          <div className="grid grid-cols-3 gap-2">
            {slotsForSelectedDate.map((slot) => (
              <button
                key={slot}
                onClick={() => handleSelectSlot(slot)}
                className="border border-gray-200 rounded-lg py-2 text-sm hover:border-primary-500 hover:bg-primary-50 transition"
              >
                {formatTime(slot, currentLang, timeZoneId)}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'details' && selectedType && selectedSlot && (
        <div>
          <BackButton onClick={() => setStep('slot')} t={t} />
          <p className="text-gray-600 mb-4">
            {title(selectedType)} — {formatDateTime(selectedSlot, currentLang, timeZoneId)}
          </p>
          {timeZoneId && <p className="text-xs text-gray-400 mb-4">{t('booking.timesShownIn', { zone: timeZoneId })}</p>}
          {submitError && <div className="error mb-4">{submitError}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">{t('booking.name')}</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">{t('booking.email')}</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">{t('booking.phone')}</span>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">{t('booking.message')}</span>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
              />
            </label>

            {/* Honeypot — hidden from real users, left empty by them; bots often fill every field */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />

            <label className="flex items-start gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1"
              />
              <span>
                {t('contact.consentPrefix')}{' '}
                <a href={legalUrls.privacy} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  {t('contact.consentPrivacyLink')}
                </a>
                {' '}{t('contact.consentSuffix')}{' '}
                {t('contact.consentTermsConnector')}{' '}
                <a href={legalUrls.terms} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  {t('contact.termsLink')}
                </a>
                {' '}{t('contact.consentCancellationConnector')}{' '}
                <a href={legalUrls.cancellation} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  {t('contact.cancellationLink')}
                </a>
                {t('contact.consentEnd')}{' *'}
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t('common.loading') : t('booking.submit')}
            </button>
          </form>
        </div>
      )}

      {step === 'success' && result && selectedType && (
        <div className="success space-y-3">
          <p className="font-medium">{t('booking.successMessage')}</p>
          <p className="text-sm">
            {title(selectedType)} — {formatDateTime(result.startUtc, currentLang, result.timeZoneId)}
          </p>
          {result.status === 'pending' && <p className="text-sm">{t('booking.pendingApprovalNote')}</p>}
          <p className="text-sm">
            <a
              href={`/${currentLang}/booking/${result.id}?token=${encodeURIComponent(result.cancelToken)}&d=${result.date}`}
              className="text-primary-700 underline"
            >
              {t('booking.manageLink')}
            </a>
          </p>
        </div>
      )}
      </div>
    </div>
  );
};

// ── Small presentational helpers ──────────────────────────────────────────────────────

type AvailabilityResponseState = Record<string, AvailabilityDay>;

const BackButton: React.FC<{ onClick: () => void; t: (key: string) => string }> = ({ onClick, t }) => (
  <button onClick={onClick} className="text-sm text-primary-700 hover:underline mb-4">
    &larr; {t('booking.back')}
  </button>
);

const StepIndicator: React.FC<{ step: Step; t: (key: string) => string }> = ({ step, t }) => {
  const steps: Step[] = ['type', 'date', 'slot', 'details'];
  const currentIndex = steps.indexOf(step);
  return (
    <div className="flex items-center gap-2 text-xs text-gray-400 mb-6">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <span className={i <= currentIndex ? 'text-primary-600 font-medium' : ''}>{t(`booking.steps.${s}`)}</span>
          {i < steps.length - 1 && <span>&rarr;</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function startOfMonthIso(date: Date): string {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  return toDateKey(start);
}

function endOfMonthIso(date: Date): string {
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return toDateKey(end);
}

// Slots are always rendered in the practice's timezone (not the visitor's browser timezone) — a
// slot offered as "10:00" must mean 10:00 at the practice, wherever the visitor happens to be.
function formatTime(iso: string, locale: string, timeZoneId?: string): string {
  try {
    return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone: timeZoneId });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string, locale: string, timeZoneId?: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: timeZoneId,
    });
  } catch {
    return iso;
  }
}

export default AppointmentScheduler;
